import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Download, ChevronRight, Star, Zap, Leaf, Brain } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const QUOTIENT_META = {
  CRQ: { label: 'Critical Reasoning', color: '#7c3aed', bg: 'bg-violet-50', text: 'text-violet-700' },
  SRQ: { label: 'Self-Regulation', color: '#2563eb', bg: 'bg-blue-50', text: 'text-blue-700' },
  LQ: { label: 'Leadership', color: '#d97706', bg: 'bg-amber-50', text: 'text-amber-700' },
  SIQ: { label: 'Social Intelligence', color: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  PEQ: { label: 'Purpose & Ethics', color: '#dc2626', bg: 'bg-red-50', text: 'text-red-700' },
  DAQ: { label: 'Digital Agility', color: '#0891b2', bg: 'bg-cyan-50', text: 'text-cyan-700' },
  SEQ: { label: 'Systems & Enterprise', color: '#7c3aed', bg: 'bg-purple-50', text: 'text-purple-700' }
};

const SRI_LEVELS = [
  { min: 300, max: 419, label: 'Foundation', color: 'from-gray-400 to-gray-500' },
  { min: 420, max: 539, label: 'Developing', color: 'from-blue-400 to-blue-500' },
  { min: 540, max: 659, label: 'Proficient', color: 'from-violet-500 to-purple-600' },
  { min: 660, max: 779, label: 'Advanced', color: 'from-amber-500 to-orange-500' },
  { min: 780, max: 900, label: 'Expert', color: 'from-emerald-500 to-teal-600' }
];

// ─── SRI Gauge ────────────────────────────────────────────────────────────────
const SRIGauge = ({ score = 300 }) => {
  const pct = ((score - 300) / 600) * 100;
  const level = SRI_LEVELS.find(l => score >= l.min && score <= l.max) || SRI_LEVELS[0];
  const angle = -140 + (pct / 100) * 280;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-52 h-32 overflow-hidden">
        <svg viewBox="0 0 200 110" className="w-full">
          {/* Background arc */}
          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#e5e7eb" strokeWidth="14" strokeLinecap="round" />
          {/* Coloured arc segments */}
          {SRI_LEVELS.map((l, i) => {
            const startPct = ((l.min - 300) / 600) * 100;
            const endPct = ((l.max - 300) / 600) * 100;
            const startAngle = -140 + (startPct / 100) * 280;
            const endAngle = -140 + (endPct / 100) * 280;
            const colors = ['#9ca3af', '#60a5fa', '#7c3aed', '#f59e0b', '#10b981'];
            const r = 80, cx = 100, cy = 100;
            const toRad = a => (a * Math.PI) / 180;
            const x1 = cx + r * Math.cos(toRad(startAngle));
            const y1 = cy + r * Math.sin(toRad(startAngle));
            const x2 = cx + r * Math.cos(toRad(endAngle));
            const y2 = cy + r * Math.sin(toRad(endAngle));
            return <path key={i} d={`M ${x1} ${y1} A 80 80 0 0 1 ${x2} ${y2}`}
              fill="none" stroke={colors[i]} strokeWidth="14" strokeLinecap="round" opacity="0.3" />;
          })}
          {/* Needle */}
          <line x1="100" y1="100" x2={100 + 65 * Math.cos(((angle) * Math.PI) / 180)}
            y2={100 + 65 * Math.sin(((angle) * Math.PI) / 180)}
            stroke="#1f2937" strokeWidth="3" strokeLinecap="round" />
          <circle cx="100" cy="100" r="6" fill="#1f2937" />
        </svg>
        <div className="absolute bottom-0 inset-x-0 text-center">
          <p className="text-4xl font-black text-gray-900">{score}</p>
          <p className="text-xs text-gray-400 font-medium">SRI Score (300–900)</p>
        </div>
      </div>
      <div className={`mt-3 px-5 py-1.5 rounded-full bg-gradient-to-r ${level.color} text-white text-sm font-bold shadow-md`}>
        {level.label}
      </div>
    </div>
  );
};

// ─── Quotient Bar ─────────────────────────────────────────────────────────────
const QuotientBar = ({ code, score }) => {
  const meta = QUOTIENT_META[code];
  if (!meta) return null;
  return (
    <div className={`${meta.bg} rounded-xl p-4 border border-gray-100`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{code}</p>
          <p className={`text-sm font-bold ${meta.text}`}>{meta.label}</p>
        </div>
        <span className={`text-2xl font-black ${meta.text}`}>{score}%</span>
      </div>
      <div className="h-2 bg-white rounded-full overflow-hidden shadow-inner">
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${score}%`, backgroundColor: meta.color }} />
      </div>
    </div>
  );
};

// ─── Standard Card ────────────────────────────────────────────────────────────
const StandardCard = ({ standard }) => {
  const stageColors = {
    Stage1_Capacity: 'bg-violet-100 text-violet-700',
    Stage2_Capability: 'bg-blue-100 text-blue-700',
    Stage3_Leadership: 'bg-amber-100 text-amber-700'
  };
  const stageLabel = { Stage1_Capacity: 'Capacity', Stage2_Capability: 'Capability', Stage3_Leadership: 'Leadership' };
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
      <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl flex items-center justify-center text-white font-black text-sm flex-shrink-0">
        {standard.standardNumber}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="font-semibold text-gray-900 text-sm truncate">{standard.title}</p>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${stageColors[standard.stage]}`}>
            {stageLabel[standard.stage]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full">
            <div className="h-full bg-gradient-to-r from-violet-600 to-purple-600 rounded-full transition-all"
              style={{ width: `${standard.score}%` }} />
          </div>
          <span className="text-xs font-bold text-violet-700">{standard.score}%</span>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const SkillsPassport = () => {
  const navigate = useNavigate();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    fetchPassport(user._id || user.id);
  }, []);

  const fetchPassport = async (studentId) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/passport/${studentId}`);
      if (data.success) setPassport(data.data);
    } catch (e) {
      if (e.response?.status !== 404) toast.error('Failed to load passport.');
    } finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
    </div>
  );

  if (!passport) return (
    <div className="max-w-lg mx-auto py-20 text-center">
      <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
        <Award className="w-10 h-10 text-gray-400" />
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-3">Skills Passport Not Yet Earned</h2>
      <p className="text-gray-500 mb-6">Complete all 25 core courses and pass the T4 gate assessment to unlock your Skills Passport.</p>
      <button onClick={() => navigate('/student/my-courses')}
        className="px-6 py-3 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700">
        Go to My Courses →
      </button>
    </div>
  );

  const tabs = ['overview', 'quotients', 'standards', 'tracks'];

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12">

      {/* ── Passport Header ─────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-violet-700 via-purple-700 to-indigo-800 rounded-3xl p-8 text-white shadow-2xl overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-5 h-5 opacity-70" />
                <span className="text-sm font-bold opacity-70">SMAART Institute</span>
              </div>
              <h1 className="text-2xl font-black">Skills Passport</h1>
              <p className="text-violet-200 text-sm">#{passport.passportId}</p>
            </div>
            <button onClick={() => toast('PDF download coming soon!')}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 transition px-4 py-2 rounded-xl text-sm font-bold">
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
          <div className="flex items-center justify-center py-2">
            <SRIGauge score={passport.SRIScore} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            {[
              ['Courses', passport.totalCoursesCompleted + '/40', 'text-violet-200'],
              ['Avg Score', passport.overallAverageScore + '%', 'text-violet-200'],
              ['Baseline', passport.baselineScore + '%', 'text-violet-200']
            ].map(([l, v, c]) => (
              <div key={l} className="bg-white/10 rounded-xl py-2 px-3">
                <p className={`text-lg font-black ${c}`}>{v}</p>
                <p className="text-xs opacity-60">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3.5 text-sm font-bold capitalize transition-colors
                ${activeTab === tab ? 'bg-violet-50 text-violet-700 border-b-2 border-violet-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
              {tab}
            </button>
          ))}
        </div>

        <div className="p-6">

          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(passport.stageSummaries || []).map(s => {
                  const colors = { Stage1_Capacity: 'from-violet-600 to-purple-700', Stage2_Capability: 'from-blue-600 to-cyan-600', Stage3_Leadership: 'from-amber-500 to-orange-600' };
                  return (
                    <div key={s.stageCode} className={`bg-gradient-to-br ${colors[s.stageCode] || 'from-gray-500 to-gray-600'} rounded-2xl p-4 text-white`}>
                      <p className="text-xs font-bold opacity-70 mb-1">{s.stageCode?.replace('_', ' — ')}</p>
                      <p className="text-3xl font-black">{s.averageScore}%</p>
                      <p className="text-xs opacity-70">{s.coursesCompleted} courses · Gate {s.gateScore}%</p>
                    </div>
                  );
                })}
              </div>
              <div className="bg-violet-50 rounded-2xl p-5 border border-violet-100">
                <h3 className="font-bold text-violet-900 mb-1">What is the SRI Score?</h3>
                <p className="text-sm text-violet-700">The <strong>SMAART Readiness Index (SRI)</strong> is your overall human intelligence score on a 300–900 scale. It combines your performance across all 7 Intelligence Quotients, weighted by the SJT sections of every assessment.</p>
              </div>
            </div>
          )}

          {/* Quotients */}
          {activeTab === 'quotients' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-2">Your performance across the 7 SMAART Intelligence Quotients</p>
              {(passport.quotients || []).map(q => (
                <QuotientBar key={q.code} code={q.code} score={q.score} />
              ))}
            </div>
          )}

          {/* Standards */}
          {activeTab === 'standards' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-2">10 Competency Standards across the 3 stages</p>
              {(passport.standards || []).map(s => <StandardCard key={s.standardNumber} standard={s} />)}
            </div>
          )}

          {/* Tracks */}
          {activeTab === 'tracks' && (
            <div className="space-y-4">
              {(passport.trackCertifications || []).map(t => {
                const meta = { PIQ: { icon: Star, color: 'from-rose-500 to-pink-600' }, AIQ: { icon: Zap, color: 'from-emerald-500 to-teal-600' }, SQ: { icon: Leaf, color: 'from-green-500 to-lime-600' } }[t.trackCode];
                const Icon = meta?.icon || Award;
                return (
                  <div key={t.trackCode} className={`rounded-2xl p-5 text-white bg-gradient-to-r ${meta?.color || 'from-gray-500 to-gray-600'} ${t.isEarned ? '' : 'opacity-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-black">{t.label}</p>
                        <p className="text-sm opacity-80">{t.isEarned ? `Earned ${new Date(t.earnedAt).toLocaleDateString()}` : 'Not yet earned'}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!passport.trackCertifications?.length && (
                <p className="text-center text-gray-400 py-6 text-sm">Complete PIQ, AIQ, or SQ tracks to earn certifications.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillsPassport;
